import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  getOnboardingCopyCatalog,
  listOnboardingConfigs,
  publishOnboardingConfig,
  restoreOnboardingConfig,
  saveOnboardingConfigDraft,
} from '@platform/client';
import { subjectSchema } from '@platform/schema/content';
import {
  DEFAULT_ONBOARDING_CONFIG,
  onboardingConfigsSchema,
  onboardingCopyCatalogSchema,
} from '@platform/schema/onboarding';
import { flowProblems } from '@platform/schema/onboarding-flow';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { ChoiceField } from '../authoring/ChoiceField';
import { EditableText } from '../authoring/EditableText';
import { subjectLabel } from '../library/subject';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import {
  addStep,
  duplicateStep,
  isSharedCopy,
  previewSample,
  replaceStep,
  reword,
  summarize,
  type TopicsReady,
} from './config';
import { labelOf } from './controls';
import { FlowPreview } from './FlowPreview';
import { OnboardingChat } from './OnboardingChat';
import { PreviewLearner } from './ScreenNotes';
import { StepEditor } from './StepEditor';
import { StepList } from './StepList';

import type { SaveOnboardingConfigDraftData } from '@platform/client';
import type { Subject } from '@platform/schema/content';
import type {
  OnboardingConfig,
  OnboardingConfigs,
  OnboardingConfigVersion,
  OnboardingCopyCatalog,
  StartCheck,
} from '@platform/schema/onboarding';

const CONFIGS_KEY = ['onboardingConfigs'];

const PROBLEM: Record<NonNullable<StartCheck['problem']>, string> = {
  NO_NEXT_PUBLISHED: 'No lesson published',
};

const slugs = (text: string): string[] =>
  text
    .split(',')
    .map((slug) => slug.trim())
    .filter((slug) => slug !== '');

type View = 'flow' | 'text' | 'starts' | 'versions';

const STEP_PANE = 320;
const EDITOR_MIN = 420;

/**
 * The Onboarding tab — ONB-05, rebuilt as a flow editor by ONB-19. Edits stay in the
 * page until Save draft; learners see nothing until the draft is published, and a roll
 * back publishes a copy of an earlier version. `docs/decisions/onboarding-flow.md`.
 */
export function Onboarding() {
  const queries = useQueryClient();
  const configs = useQuery({
    queryKey: CONFIGS_KEY,
    queryFn: async () => {
      const { data } = await listOnboardingConfigs({ throwOnError: true });
      return onboardingConfigsSchema.parse(data);
    },
  });
  const catalog = useQuery({
    queryKey: ['onboardingCopy'],
    queryFn: async () => {
      const { data } = await getOnboardingCopyCatalog({ throwOnError: true });
      return onboardingCopyCatalogSchema.parse(data);
    },
    staleTime: Infinity,
  });
  const changed = () => queries.invalidateQueries({ queryKey: CONFIGS_KEY });

  const [unsaved, setUnsaved] = useState<OnboardingConfig>();
  const [view, setView] = useState<View>('flow');
  const [selected, setSelected] = useState('WELCOME');
  const [journey, setJourney] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [previewing, setPreviewing] = useState<OnboardingConfigVersion>();
  const [confirming, setConfirming] = useState(false);
  const [asking, setAsking] = useState(false);
  const [topicsReady, setTopicsReady] = useState<TopicsReady>('ALL');

  const save = useTrackedMutation('Save the onboarding draft', {
    mutationFn: async (body: OnboardingConfig) => {
      await saveOnboardingConfigDraft({
        // A cleared optional field is an `undefined` key here and absent on the wire;
        // the generated type, under `exactOptionalPropertyTypes`, only names the second.
        body: body as SaveOnboardingConfigDraftData['body'],
        throwOnError: true,
      });
    },
    onSuccess: () => {
      setUnsaved(undefined);
    },
    onSettled: changed,
  });
  const publish = useTrackedMutation('Publish onboarding', {
    mutationFn: async () => {
      await publishOnboardingConfig({ throwOnError: true });
    },
    onSuccess: () => {
      setConfirming(false);
    },
    onSettled: changed,
  });
  const restore = useTrackedMutation('Roll back onboarding', {
    mutationFn: async (version: number) => {
      await restoreOnboardingConfig({ body: { version }, throwOnError: true });
    },
    onSettled: changed,
  });

  if (configs.isError || catalog.isError) {
    return (
      <Alert severity="error">
        {describeApiError(configs.error ?? catalog.error)}
      </Alert>
    );
  }
  if (configs.data === undefined || catalog.data === undefined) {
    return <CircularProgress aria-label="Loading onboarding" />;
  }

  const { live, versions, challenge } = configs.data;
  const config = unsaved ?? configs.data.editing;
  const edit = (next: OnboardingConfig) => {
    setUnsaved(next);
    setPreviewing(undefined);
  };
  const hasDraft = versions.some((row) => row.status === 'DRAFT');
  const busy = save.isPending || publish.isPending || restore.isPending;
  const problems = flowProblems(config.flow, challenge?.questions.length);
  const blocking = problems.filter((one) => one.level === 'block');
  const step = config.flow.find((one) => one.id === selected) ?? config.flow[0];
  const liveConfig =
    versions.find((row) => row.version === live)?.config ??
    DEFAULT_ONBOARDING_CONFIG;
  const previewStep = journey || previewing ? undefined : step?.id;
  const focusPreview = previewStep === 'STARTING_FOCUS';
  const sample = previewSample(
    (previewing?.config ?? config).flow,
    focusPreview ? topicsReady : 'ALL',
  );
  const why =
    unsaved !== undefined
      ? 'Save the draft before publishing it.'
      : !hasDraft
        ? 'There is no draft: edit something first.'
        : blocking.length > 0
          ? `${String(blocking.length)} problem(s) must be fixed first; the steps marked Fix say where.`
          : undefined;

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader
        title="Onboarding"
        subtitle={
          (live === null
            ? 'Learners get the app’s built-in onboarding.'
            : `Learners get version ${String(live)}.`) +
          (unsaved !== undefined
            ? ' You have unsaved changes.'
            : hasDraft
              ? ' You are editing a saved draft.'
              : ' Any edit starts a draft.')
        }
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={unsaved === undefined || busy}
              onClick={() => {
                if (unsaved !== undefined) save.mutate(unsaved);
              }}
            >
              Save draft
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setView('flow');
                setJourney(true);
                setNonce(nonce + 1);
              }}
            >
              Preview onboarding
            </Button>
            <Button
              variant={asking ? 'contained' : 'outlined'}
              onClick={() => {
                setView('flow');
                setAsking(!asking);
              }}
            >
              Ask the planner
            </Button>
            <Button
              variant="contained"
              disabled={why !== undefined || busy}
              onClick={() => {
                setConfirming(true);
              }}
            >
              Publish changes
            </Button>
          </Stack>
        }
      />
      {why !== undefined && hasDraft && (
        <Typography variant="body2" color="text.secondary">
          Publish is unavailable: {why}
        </Typography>
      )}
      {save.isError && (
        <Alert severity="error">{describeApiError(save.error)}</Alert>
      )}

      <Tabs
        value={view}
        onChange={(_, next: View) => {
          setView(next);
        }}
      >
        <Tab value="flow" label="Screens" />
        <Tab value="text" label="Shared text" />
        <Tab value="starts" label="Topic starts" />
        <Tab value="versions" label="Versions" />
      </Tabs>

      {view === 'flow' && step !== undefined && (
        <Stack
          direction="row"
          spacing={3}
          sx={{ alignItems: 'flex-start', position: 'relative' }}
        >
          <Stack spacing={1} sx={{ width: STEP_PANE, flexShrink: 0 }}>
            <StepList
              flow={config.flow}
              selected={step.id}
              problems={problems}
              challenge={challenge}
              disabled={busy}
              onSelect={(id) => {
                setSelected(id);
                setJourney(false);
              }}
              onChange={(flow) => {
                edit({ ...config, flow });
              }}
            />
            {(['INFO', 'QUESTION'] as const).map((kind) => (
              <Button
                key={kind}
                variant="outlined"
                size="small"
                disabled={busy}
                onClick={() => {
                  const added = addStep(
                    config.flow,
                    kind,
                    config.flow.indexOf(step),
                  );
                  edit({ ...config, flow: added.flow });
                  setSelected(added.id);
                  setJourney(false);
                }}
              >
                {kind === 'INFO'
                  ? 'Add an information screen'
                  : 'Add a question'}
              </Button>
            ))}
          </Stack>
          <Box sx={{ flex: 1, minWidth: EDITOR_MIN }}>
            <StepEditor
              step={step}
              config={config}
              catalog={catalog.data}
              problems={problems.filter((one) => one.step === step.id)}
              challenge={challenge}
              disabled={busy}
              onConfig={edit}
              onStep={(next) => {
                edit({ ...config, flow: replaceStep(config.flow, next) });
              }}
              onDuplicate={() => {
                const copy = duplicateStep(config.flow, step.id);
                edit({ ...config, flow: copy.flow });
                setSelected(copy.id);
              }}
              onRemove={() => {
                const at = config.flow.indexOf(step);
                edit({
                  ...config,
                  flow: config.flow.filter((one) => one.id !== step.id),
                });
                setSelected(config.flow[at - 1]?.id ?? 'WELCOME');
              }}
            />
          </Box>
          <Stack spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={journey ? 'text' : 'contained'}
                onClick={() => {
                  setJourney(false);
                }}
              >
                This screen
              </Button>
              <Button
                size="small"
                variant={journey ? 'contained' : 'text'}
                onClick={() => {
                  setJourney(true);
                }}
              >
                Whole journey
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setNonce(nonce + 1);
                }}
              >
                Restart
              </Button>
            </Stack>
            {previewing !== undefined && (
              <Alert
                severity="info"
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      setPreviewing(undefined);
                    }}
                  >
                    Back to the draft
                  </Button>
                }
              >
                Previewing version {previewing.version}
              </Alert>
            )}
            <PreviewLearner
              sample={sample}
              ready={focusPreview ? topicsReady : undefined}
              onReady={setTopicsReady}
              goalName={(goal) =>
                catalog.data.entries.find((row) => row.key === `goal.${goal}`)
                  ?.english ?? goal
              }
            />
            <FlowPreview
              config={previewing?.config ?? config}
              stepId={previewStep}
              nonce={nonce}
              challengeId={challenge?.lessonId}
              topicsReady={focusPreview ? topicsReady : 'ALL'}
            />
          </Stack>
          <OnboardingChat
            open={asking}
            step={step}
            config={config}
            catalog={catalog.data}
            disabled={busy}
            onConfig={edit}
            onClose={() => {
              setAsking(false);
            }}
          />
        </Stack>
      )}

      {view === 'text' && (
        <SharedText
          catalog={catalog.data}
          config={config}
          disabled={busy}
          onChange={edit}
        />
      )}
      {view === 'starts' && (
        <StartsEditor
          config={config}
          checks={configs.data.checks}
          disabled={busy}
          onChange={edit}
        />
      )}
      {view === 'versions' && (
        <Versions
          configs={configs.data}
          disabled={busy}
          onPreview={(row) => {
            setPreviewing(row);
            setView('flow');
            setJourney(true);
            setNonce(nonce + 1);
          }}
          onRestore={(version) => {
            restore.mutate(version);
          }}
        />
      )}

      <Dialog
        open={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish these changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            New onboarding sessions get them at once. A learner already part way
            through keeps the screens they started with.
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ pl: 3 }}>
            {summarize(liveConfig, config, catalog.data).map((line) => (
              <Typography component="li" variant="body2" key={line}>
                {line}
              </Typography>
            ))}
          </Stack>
          {problems.length > 0 && (
            <Alert severity="warning">
              {String(problems.length)} warning(s) remain; none blocks
              publishing.
            </Alert>
          )}
          {publish.isError && (
            <Alert severity="error">{describeApiError(publish.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirming(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => {
              publish.mutate();
            }}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** Words several screens share: Back, the phase names, loading, and Home's heading. */
function SharedText({
  catalog,
  config,
  disabled,
  onChange,
}: {
  catalog: OnboardingCopyCatalog;
  config: OnboardingConfig;
  disabled: boolean;
  onChange: (next: OnboardingConfig) => void;
}) {
  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MIN * 2 }}>
      <Typography variant="body2" color="text.secondary">
        Keep each {'{{value}}'}: the app fills it in. Each screen’s own text is
        on the Screens tab.
      </Typography>
      {catalog.entries
        .filter((row) => isSharedCopy(row.key))
        .map(({ key, english }) => {
          const text = config.copy.find((row) => row.key === key)?.text;
          return (
            <EditableText
              key={`${key}:${text ?? ''}`}
              label={`${labelOf(key)} (${key})`}
              value={text ?? english}
              multiline
              disabled={disabled}
              onSave={(next) => {
                onChange(reword(config, key, next, english));
              }}
            />
          );
        })}
    </Stack>
  );
}

function StartsEditor({
  config,
  checks,
  disabled,
  onChange,
}: {
  config: OnboardingConfig;
  checks: StartCheck[];
  disabled: boolean;
  onChange: (next: OnboardingConfig) => void;
}) {
  const unmapped = subjectSchema.options.filter(
    (topic) => !config.starts.some((start) => start.topic === topic),
  );
  const [topic, setTopic] = useState<Subject | undefined>(undefined);
  const [next, setNext] = useState('');

  const replace = (at: Subject, change: Partial<(typeof config.starts)[0]>) => {
    onChange({
      ...config,
      starts: config.starts.map((start) =>
        start.topic === at ? { ...start, ...change } : start,
      ),
    });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Topic starts</Typography>
      <Typography variant="body2" color="text.secondary">
        Which lessons open each topic, by slug, after the first challenge. The
        challenge is the same for every learner and is edited on the Screens
        tab, under Your first challenge. A topic can be picked as a start only
        while one of its lessons is published; until then learners see it as
        Coming soon. The check reads the draft against content as it is now.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Topic</TableCell>
            <TableCell>Opening lessons</TableCell>
            <TableCell>Check</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {config.starts.map((start) => {
            const problem = checks.find(
              (check) => check.topic === start.topic,
            )?.problem;
            return (
              <TableRow key={start.topic}>
                <TableCell>{subjectLabel(start.topic)}</TableCell>
                <TableCell>
                  <EditableText
                    key={start.next.join()}
                    label="Slugs, comma separated"
                    value={start.next.join(', ')}
                    disabled={disabled}
                    onSave={(text) => {
                      replace(start.topic, { next: slugs(text) });
                    }}
                  />
                </TableCell>
                <TableCell>
                  {problem === undefined ? null : problem === null ? (
                    <Chip size="small" color="success" label="Startable" />
                  ) : (
                    <Chip
                      size="small"
                      color="warning"
                      label={PROBLEM[problem]}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    disabled={disabled}
                    onClick={() => {
                      onChange({
                        ...config,
                        starts: config.starts.filter(
                          (one) => one.topic !== start.topic,
                        ),
                      });
                    }}
                  >
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <ChoiceField
          label="Topic"
          value={topic}
          options={unmapped}
          describe={subjectLabel}
          disabled={disabled}
          onChange={setTopic}
        />
        <TextField
          size="small"
          label="Opening lesson slugs"
          value={next}
          onChange={(event) => {
            setNext(event.target.value);
          }}
        />
        <Button
          disabled={disabled || topic === undefined || slugs(next).length === 0}
          onClick={() => {
            if (topic === undefined) return;
            onChange({
              ...config,
              starts: [...config.starts, { topic, next: slugs(next) }],
            });
            setTopic(undefined);
            setNext('');
          }}
        >
          Add topic
        </Button>
      </Stack>
    </Stack>
  );
}

function Versions({
  configs,
  disabled,
  onPreview,
  onRestore,
}: {
  configs: OnboardingConfigs;
  disabled: boolean;
  onPreview: (row: OnboardingConfigVersion) => void;
  onRestore: (version: number) => void;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Versions</Typography>
      <Table size="small" aria-label="Onboarding versions">
        <TableHead>
          <TableRow>
            <TableCell>Version</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Published</TableCell>
            <TableCell>Reworded strings</TableCell>
            <TableCell>Screens shown</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {configs.versions.map((row) => {
            const live = row.version === configs.live;
            return (
              <TableRow key={row.version}>
                <TableCell>
                  {row.version}
                  {row.restoredFrom !== null &&
                    ` (copy of ${String(row.restoredFrom)})`}
                </TableCell>
                <TableCell>
                  {live ? (
                    <Chip size="small" color="success" label="Live" />
                  ) : (
                    row.status.toLowerCase()
                  )}
                </TableCell>
                <TableCell>
                  {row.publishedAt === null
                    ? '—'
                    : new Date(row.publishedAt).toLocaleString()}
                </TableCell>
                <TableCell>{row.config.copy.length}</TableCell>
                <TableCell>
                  {row.config.flow.filter((one) => one.enabled).length}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    onClick={() => {
                      onPreview(row);
                    }}
                  >
                    Preview
                  </Button>
                  {row.status === 'PUBLISHED' && !live && (
                    <Button
                      size="small"
                      disabled={disabled}
                      onClick={() => {
                        onRestore(row.version);
                      }}
                    >
                      Roll back to this
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {configs.versions.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nothing published yet: learners get the built-in config.
        </Typography>
      )}
    </Stack>
  );
}
